import React from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {Text, useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui'

/**
 * Component that displays current price of a product with a11y
 * @param price - price of the product
 * @param as - an HTML tag or component to be rendered as
 * @param isRange - show price as range or not
 * @param currency - currency to show the price in
 * @param extraProps - extra props to be passed into Text Component
 * @returns {JSX.Element}
 */
const FormatPrice = ({price, currency, ...extraProps}) => {
    const intl = useIntl()
    const styles = useMultiStyleConfig('formatPrice')

    const currencySymbol = intl
        .formatNumber(0, {
            style: 'currency',
            currency,
            currencyDisplay: 'symbol',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        })
        .replace(/[\d.,\s]/g, '')

    const isItalianLocale = intl.locale.startsWith('it')
    let formattedPrice
    if (price !== undefined && price !== null) {
        if (isItalianLocale) {
            formattedPrice = intl.formatNumber(price, {
                style: 'decimal',
                minimumFractionDigits: price % 1 === 0 ? 0 : 2,
                maximumFractionDigits: 2
            })
        } else {
            formattedPrice = new Intl.NumberFormat(price, {
                style: 'decimal',
                minimumFractionDigits: price % 1 === 0 ? 0 : 2,
                maximumFractionDigits: 2
            }).format(price)
        }
    }

    return (
        <Text as="span" {...extraProps} className="price" {...styles.formatPrice.align}>
            <Text as="span" className="currency-symbol">
                {currencySymbol}
            </Text>
            <Text as="span" className="price-number">
                {formattedPrice}
            </Text>
        </Text>
    )
}

FormatPrice.propTypes = {
    price: PropTypes.number.isRequired,
    currency: PropTypes.string.isRequired
}

export default FormatPrice
